/**
 * @file       CyAccess service for logged in user
 * @version    2.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */

import { Injectable } from '@angular/core';
import { ERROR } from '@app/constants/Error.constant';
import { ACCESS } from '@app/constants/Api.constant';
//import { debounce } from 'rxjs/operators';
import { Observable, catchError, map, of } from 'rxjs';

import { ApiService } from '@app/secure/service/Api.service';
import { CyAccessData, CyAccessResponse } from '@app/secure/interface/CyAccessServiceResponses.interface';
import { CyResponse } from '@app/secure/interface/CyResponse.interface';

export interface CyAccess 
{
    name: string;
}

@Injectable
({
    providedIn: 'root'
})

class CyAccessService 
{
    constructor
    (
        private apiService: ApiService
    ) 
    {}

    public createCaseManagementSession
    (
        uId: string
    ) : Observable<CyAccessResponse>
    {
        let cyResult: CyAccessResponse;
        let errorParts: string[];
        let postData: Record<string, string | boolean>;
        let serviceObj: Observable<CyAccessResponse>;

        postData =
        {
            uId: uId
        };

        serviceObj = this.apiService
            .post<CyAccessResponse>(
                'access/createCaseManagementSession',
                postData)
        .pipe(map((data: CyAccessResponse) =>
        {
            cyResult =
            {
                cyData: {},
                errorCode: ERROR.NO_ERROR,
                errorMessage: null
            };
            cyResult.cyData = data;

            return cyResult;
        }),
        catchError((err) =>
        {
            errorParts = err.message.split(',');
            cyResult = 
            { 
                updated: false,
                errorCode: null,
                errorMessage: null 
            };

            if (errorParts && errorParts.length > 1 && errorParts[0])
            {
                let errorCodeNum: number;
                errorCodeNum = Number(errorParts[1]);
                switch (errorCodeNum)
                {
                    case ERROR.GENERAL_DB:
                        cyResult.errorCode = errorParts[1];
                        cyResult.errorMessage = 'General DB Error';
                        break;
                    default:
                        cyResult.errorCode = errorParts[1];
                        cyResult.errorMessage = 'General Error';
                        break;
                }
            }

            return of(cyResult);
        }));

        return serviceObj;        
    }

    async addUpdateCyAccess
    (
        uId: String
    ): Promise<CyAccessResponse | undefined>
    {
        let result: CyAccessResponse | undefined;
        let data: Record<string, string>;
        let response: CyAccessResponse | undefined;

        result = undefined;
        data =
        {
            uId: String(uId)
        };
        try
        {
            let rawResp: CyResponse<CyAccessData> | undefined;
            rawResp = await this.apiService.postRaw<CyAccessData>(
                ACCESS.ADD_UPDATE_CYPHER_ACCESS,
                data
            ).toPromise();
            if (rawResp && typeof rawResp === 'object')
            {
                response =
                {
                    cyData: rawResp.cyData,
                    error: rawResp.error,
                    errorMsg: rawResp.errorMsg ?? null
                };
            }
            else
            {
                response = undefined;
            }

            if (
                response &&
                typeof response === 'object' &&
                (
                    ('cyData' in response &&
                     response.cyData &&
                     typeof response.cyData === 'object' &&
                     'accessKey' in response.cyData) ||
                    ('error' in response && response.error === ERROR.NO_ERROR)
                )
            )
            {
                result = response;
            }
        }
        catch (error)
        {
            console.log('Error: ', error);
        }

        return result;
    }

    public deleteAccess
    (
        uId: string
    ) : Observable<CyAccessResponse>
    {
        let cyResult: CyAccessResponse;
        let errorParts: string[];
        let postData: Record<string, string>;
        let serviceObj: Observable<CyAccessResponse>;

        postData =
        {
            uId: uId
        }

        serviceObj = this.apiService
            .post<CyAccessResponse>(
                ACCESS
                    .DELETE_ACCESS,
                postData)
        .pipe(map((data: CyAccessResponse) =>
        {
            cyResult =
            {
                cyData: {},
                errorCode: ERROR.NO_ERROR,
                errorMessage: null
            };
            cyResult.cyData = data;

            return cyResult;
        }),
        catchError((err) =>
        {
            errorParts = err.message.split(',');
            cyResult =
            {
                deleted: false,
                errorCode: null,
                errorMessage: null
            };

            if (errorParts &&
                errorParts.length > 1 &&
                errorParts[0])
            {
                cyResult.errorCode =
                    errorParts[1];
                cyResult.errorMessage =
                    'Delete access error';
            }

            return of(cyResult);
        }));

        return serviceObj;
    }
}

export { CyAccessService };
